import { useEffect, useMemo } from 'react';
import { useVisitStore, selectFilteredVisits } from '@/lib/stores/visit-store';
import { VisitCard } from '@/components/visits/VisitCard';
import { CardSkeleton } from '@/components/ui/SkeletonLoader';
import type { VisitStatus } from '@assistmd/types';

const STATUS_OPTIONS: Array<{ value: VisitStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All Visits' },
  { value: 'recording', label: 'Recording' },
  { value: 'draft', label: 'Draft' },
  { value: 'finalizing', label: 'Finalizing' },
  { value: 'closed', label: 'Closed' },
];

/** Status dot color mapping */
const STATUS_DOT_COLORS: Record<string, string> = {
  all: 'bg-slate-400',
  recording: 'bg-emerald-400',
  draft: 'bg-amber-400',
  finalizing: 'bg-sky-400',
  closed: 'bg-slate-300',
};

/**
 * Visit list page — editorial aesthetic with staggered reveals,
 * refined filters, and a richer empty state.
 */
export function VisitsPage(): JSX.Element {
  const {
    loading,
    error,
    searchQuery,
    statusFilter,
    setSearchQuery,
    setStatusFilter,
    fetchVisits,
  } = useVisitStore();
  const filteredVisits = useVisitStore(selectFilteredVisits);
  const allVisits = useVisitStore((s) => s.visits);

  // Fetch visits from real API on mount
  useEffect(() => {
    void fetchVisits();
  }, [fetchVisits]);

  // Count visits per status for badge display
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allVisits.length };
    for (const visit of allVisits) {
      counts[visit.status] = (counts[visit.status] ?? 0) + 1;
    }
    return counts;
  }, [allVisits]);

  // Stagger classes for first 5 cards, then instant for the rest
  const getStaggerClass = (index: number): string => {
    if (index < 5) return `animate-stagger-${index + 1}`;
    return 'animate-fade-in';
  };

  return (
    <div className="animate-fade-in">
      {/* ── Premium Header ── */}
      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          {/* Icon container */}
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-teal-50 to-teal-100/50 ring-1 ring-teal-200/60 shadow-sm flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>

          <div>
            <p className="overline mb-1">Clinical Dashboard</p>
            <h1 className="page-title-serif">Visits</h1>
            <p className="mt-1.5 text-sm text-slate-500">
              Monitor and manage active clinical sessions
            </p>
          </div>
        </div>

        <div className="divider-gradient" />
      </div>

      {/* ── Metric Summary Row ── */}
      {allVisits.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6 animate-stagger-1">
          {/* Total */}
          <div className="metric-card">
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="h-3.5 w-3.5 text-teal-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
              <p className="metric-label">Total Visits</p>
            </div>
            <p className="metric-value">{allVisits.length}</p>
          </div>

          {/* Active / Recording */}
          <div className="metric-card">
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="h-3.5 w-3.5 text-emerald-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
              </svg>
              <p className="metric-label">Active</p>
            </div>
            <p className="metric-value text-teal-600">
              {statusCounts['recording'] ?? 0}
            </p>
          </div>

          {/* Closed */}
          <div className="metric-card">
            <div className="flex items-center gap-2 mb-1.5">
              <svg className="h-3.5 w-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="metric-label">Closed</p>
            </div>
            <p className="metric-value">{statusCounts['closed'] ?? 0}</p>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="mb-6 flex flex-col gap-4">
        {/* Search */}
        <div className="group relative max-w-xs">
          <svg
            className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 transition-colors duration-300 group-focus-within:text-teal-600 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            type="text"
            placeholder="Search patients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-subtle w-full pl-10"
          />
        </div>

        {/* Status filter tabs -- frosted segmented control */}
        <div className="tab-bar">
          {STATUS_OPTIONS.map((opt) => {
            const count = statusCounts[opt.value] ?? 0;
            const isActive = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className="tab-bar-item"
                data-active={isActive}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT_COLORS[opt.value] ?? 'bg-slate-400'}`} />
                {opt.label}
                {count > 0 && (
                  <span
                    className={`inline-flex items-center justify-center h-4 min-w-[16px] rounded-full px-1 text-[10px] font-semibold leading-none ${
                      isActive
                        ? 'badge-teal'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 animate-fade-in">
          <svg className="h-5 w-5 flex-shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      )}

      {/* ── Visit list ── */}
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={getStaggerClass(i)}>
              <CardSkeleton />
            </div>
          ))}
        </div>
      ) : filteredVisits.length === 0 ? (
        /* ── Premium Empty State ── */
        <div className="empty-state card rounded-2xl py-20 relative overflow-hidden">
          {/* Ambient gradient orbs */}
          <div className="absolute -left-20 -top-20 h-[300px] w-[300px] rounded-full bg-teal-200/30 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 h-[250px] w-[250px] rounded-full bg-cyan-200/20 blur-3xl" />

          {/* Icon */}
          <div className="empty-state-icon relative h-14 w-14 rounded-2xl mb-4">
            <svg
              className="h-7 w-7 animate-breathe"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.2}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          </div>

          <p className="empty-state-title relative text-base">
            No visits found
          </p>
          {searchQuery ? (
            <p className="empty-state-desc relative mt-2 max-w-[280px] text-center">
              No results match <span className="font-medium text-slate-700">&ldquo;{searchQuery}&rdquo;</span>. Try adjusting your search or filters.
            </p>
          ) : (
            <p className="empty-state-desc relative mt-2 max-w-[280px] text-center">
              Visits will appear here once clinical sessions begin recording
            </p>
          )}

          {/* Subtle footer line */}
          <div className="divider-gradient mt-8 w-48" />
        </div>
      ) : (
        <div className="space-y-2">
          {filteredVisits.map((visit, index) => (
            <div key={visit.visitId} className={getStaggerClass(index)}>
              <VisitCard visit={visit} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
